import { App as CapacitorApp } from '@capacitor/app';
import { ThemeProvider } from '@mui/material/styles';
import { Language } from '@nx-smartmonkey/shared/domain';
import { RoutalTheme } from '@nx-smartmonkey/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { RawIntlProvider } from 'react-intl';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';

import { capacitorService } from '../application/CapacitorService';
import { DriversLayout } from '../components/DriversLayout';
import { LoaderPage } from '../components/LoaderPage';
import { SnackbarComponent } from '../components/SnackbarComponent';
import { UpdateAppModal } from '../components/UpdateAppModal';
import * as routes from '../constants/Routing';
import { intl } from '../helpers/IntGlobalProvider';
import { useRetrieveTranslation } from '../hooks/translations/useRetrieveTranslation';
import { useErrorMiddleware } from '../hooks/useErrorMiddleware';
import { ExpiredSubscription } from './app/components/ExpiredSubscription';
import { AppContextProvider } from './app/context/appContext';
import { NotificationContextProvider } from './app/context/notificationContext';
import { PendingReportsContextProvider } from './app/context/pendingReports';
import { MagicLink } from './app/MagicLink';
import { PrivateComponent } from './app/PrivateComponent';
import { RouteContextProvider } from './app/route/context';
import { Auth } from './auth';
import { Logout } from './Logout';

export const Drivers = () => {
  const queryClient = useQueryClient();
  const { execute: onError } = useErrorMiddleware();
  const navigate = useNavigate();
  const { activeLanguage, isLoading: isRetrievingTranslation, run: retrieveTranslation } = useRetrieveTranslation();
  const [defaultIntl, setDefaultIntl] = useState(intl);

  useEffect(() => {
    queryClient.setDefaultOptions({
      queries: {
        networkMode: `offlineFirst`,
        onError,
      },
      mutations: {
        networkMode: `offlineFirst`,
        onError,
      },
    });
  }, [onError, queryClient]);

  useEffect(() => {
    CapacitorApp.addListener(`appUrlOpen`, (event: any) => {
      // Example: event.url = 'https://drivers.routal.com/whatever/link'
      const url = new URL(event.url);
      // Ensure this is your domain
      if (url.host === `drivers.routal.com`) {
        // Navigate within your app
        navigate({
          pathname: url.pathname,
          search: url.search,
        });
      }
    });
    return () => {
      CapacitorApp.removeAllListeners();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeLanguage) {
      setDefaultIntl(activeLanguage);
    }
  }, [activeLanguage]);

  if (!activeLanguage) {
    if (!isRetrievingTranslation) {
      retrieveTranslation({ language: Language.fromNavigatorLanguage(navigator.language) });
    }
    return (
      <ThemeProvider theme={RoutalTheme}>
        <RawIntlProvider value={defaultIntl}>
          <DriversLayout>
            <LoaderPage showText={false} />
          </DriversLayout>
        </RawIntlProvider>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={RoutalTheme}>
      <RawIntlProvider value={defaultIntl}>
        <PendingReportsContextProvider>
          <DriversLayout>
            <SnackbarComponent />
            {capacitorService.isNative() && <UpdateAppModal />}
            <Routes>
              <Route path={routes.AUTH} element={<Auth />} />
              <Route path={routes.MAGIC_LINK} element={<MagicLink />} />
              <Route
                path={routes.ROOT}
                element={
                  <AppContextProvider>
                    <NotificationContextProvider>
                      <RouteContextProvider>
                        <PrivateComponent />
                      </RouteContextProvider>
                    </NotificationContextProvider>
                  </AppContextProvider>
                }
              />
              <Route path={routes.LOGOUT} element={<Logout />} />
              <Route path={routes.SUBSCRIPTION_EXPIRED} element={<ExpiredSubscription />} />
              <Route path={routes.NOMATCHES} element={<Navigate replace to={routes.AUTH} />} />
            </Routes>
          </DriversLayout>
        </PendingReportsContextProvider>
      </RawIntlProvider>
    </ThemeProvider>
  );
};
